import React, {createRef, useState, useContext, useEffect, useCallback, useRef} from 'react';
import { useQueryClient } from 'react-query';
import {useAuth0} from "@auth0/auth0-react";
import __ from "lodash";
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import MaterialTable from "@material-table/core";
import SystemContext from "../../../SystemContext";
import { getApiSitePrefix } from "../../../functions/SiteDefaults";
import { defaultComponents, MaterialTableBody, MaterialTableBodyRow } from '../../../controls/Material-Table-Components';
import {ColumnWidth, ValidatorColumn} from "../../../blocks/ColumnHelper";
import Validator from "../validators/ContractRouteSectorsValidator";
import {MaskedNumber} from '../../../controls';
import AirportLookup from "../../Airports/components/AirportsLookup";
import { useTheme } from '@material-ui/core';

const SectionHeading = (props) => {
  const { children, ...other } = props;
  return (
    <div style={{marginTop: 10, marginBottom: 2, textAlign: "left", fontSize: "14px", fontWeight: "bold", ...other}}>
      {children}
    </div>
  );
}

const ContractRouteSectorEditor = (props) => {
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const tableRef = createRef({state: {lastEditingRow: false}});
  const {isAuthenticated} = useAuth0();
  const eventBus = SystemData.eventBus;
  const {record, readOnly, selectedRouteUuid, selectedRouteName} = props;

  useEffect(()=> {
    const addRoute = async () => {
      if (tableRef  && tableRef.current) {
        tableRef.current.dataManager.changeRowEditing();
        tableRef.current.setState({
          ...tableRef.current.dataManager.getRenderState(),
          showAddRow: !tableRef.current.state.showAddRow,
        });
      }
  }
    if (eventBus) {
      eventBus.bind('ContractRouteSectorEditor.addSector', addRoute);
      return () => {
        eventBus.unbind("ContractRouteSectorEditor.addSector", addRoute);
      }
    }

  }, [eventBus, tableRef]);

  const {classes, defaultValues} = props;

  const [state] = useState({
    canEdit: props.canEdit,
    activeRow: null
  });

  const contractRouteSectors = __.filter(record.sectors, {fuelAnnexSectorUuid: selectedRouteUuid});

  const getColumns = (props) => {
    const baseColumn = {
      cellStyle: { padding: "8px 2px" },
      headerStyle: { padding: "2px 2px" },
      editable: state.canEdit?"always":"never",
      emptyValue: "",
      initialEditValue: "",
      sorting: false
    };
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    const validatorColumn = ValidatorColumn(Validator, false);

    return [
      {...validatorColumn, resizable: false},
      {
        ...baseColumn,
        ...ColumnWidth(30,[5,2],undefined,undefined,baseColumn.headerStyle,undefined,false),
        title: "#",
        field: "sequence",
        emptyValue: null,
        initialEditValue: null,
        editable: props.canEdit ? "always" : "never",
        editComponent: (props) => (
          <MaskedNumber
            format={"#".repeat(2)}
            value={props.value}
            decimalScale={0}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.sequence}
            decimalScale={0}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(80,2,undefined,undefined,baseColumn.headerStyle,undefined,false),
        title: "Origin",
        field: "originAirportCode",
        editComponent: (props) => (
          <AirportLookup
            value={props.value}
            returnType="object"
            onChange={(returnObject)=>{
              if (returnObject.found) {
                const data = {
                  ...props.rowData,
                  originAirportCode: returnObject.data.airportCode,
                }
                props.onRowDataChange(data);
              } else {
                props.onChange(returnObject.value);
              }
            }}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(80,2,undefined,undefined,baseColumn.headerStyle,undefined,false),
        title: "Destination",
        field: "destinationAirportCode",
        editComponent: (props) => (
          <AirportLookup
            value={props.value}
            returnType="object"
            onChange={(returnObject)=>{
              if (returnObject.found) {
                const data = {
                  ...props.rowData,
                  destinationAirportCode: returnObject.data.airportCode,
                }
                props.onRowDataChange(data);
              } else {
                props.onChange(returnObject.value);
              }
            }}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(150,2,undefined,undefined,baseColumn.headerStyle,undefined,false),
        title: "Fuel Uplift Amount",
        field: "upliftAmount",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,
        editComponent: (props) => (
          <MaskedNumber
            value={props.value}
            decimalScale={0}
            fixedDecimalScale={true}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.upliftAmount}
            decimalScale={0}
            fixedDecimalScale={true}
          />
        )
      },
      {
        ...baseColumn,
        // ...ColumnWidth(undefined,2,undefined,{width: 6, minWidth: 6, maxWidth: 6},baseColumn.headerStyle,undefined,"NONE"),
        title: " ",
        field: "",
        render: ()=>(<></>),
        editable: "never",
      },
    ];
  };


  const bodyRef = createRef();

  return (isAuthenticated &&
        <div className={classes.contractRouteSectorEditor}>
          <SectionHeading>{`Route Sectors${selectedRouteName !== "" ? ` of ${selectedRouteName}` : ""}`}</SectionHeading>
          <MaterialTable
            tableRef={tableRef}
            data={contractRouteSectors}
            style={{
              backgroundColor: "#fdfdfd",
              boxShadow: "none",
              border: "2px solid #aaaaaa",
              borderRadius: 0,
            }}
            options={{
              actionsColumnIndex: -1,
              search: false,
              cellStyle: {
                padding: "8px 2px",
              },
              headerStyle: {
                backgroundColor: theme.palette.childTableHeader.backgroundColor,
                color: theme.palette.childTableHeader.textColor,
                padding: "2px",
              },
              toolbarButtonAlignment: "right",
              detailPanelType: "single",
              showTitle: false,
              toolbar: false,
              padding: 'dense',
              paging: false,
              filtering: false,
              draggable: false,
              onEditModeChanged: (editMode)=> {
                if (props.changeButtonState) {
                  const changes = {
                    save: {disabled: false, hidden: false},
                  }
                  if (editMode) {
                    changes['save'] = {disabled: true, hidden: false}
                  }
                  props.changeButtonState(changes);
                }
              },
            }}
            components={{
              ...defaultComponents,
              Body: (props) => (
                     <MaterialTableBody
                      {...props}
                      ref={bodyRef}
                      rowHeight={39}
                      overscanRowCount={10}
                      // addVirtualScrolling={true}
                      // bodyHeight={300}
                      extraEditHeight={50}
                      headerHeight={46}
                    />
              ),
              Row: (props) => (
                <MaterialTableBodyRow {...props} />
              ),
            }}
            actions={[]}
            title="Contract Routes"
            columns={getColumns({...props, canEdit: state.canEdit})}
            editable={{
              isEditable: () => {
                return state.canEdit && !readOnly;
              },
              isDeletable: () => {
                return state.canEdit && !readOnly;
              },
              onRowAdd: state.canEdit && !readOnly
                ? (newData) => new Promise((resolve, reject) => {
                  if (Array.isArray(contractRouteSectors)) {
                    const newRoute = {...newData, fuelAnnexRouteUuid: uuidv4(), fuelAnnexSectorUuid: selectedRouteUuid};
                    contractRouteSectors.push(newRoute);
                    props.onChange({fuelAnnexSectorUuid: selectedRouteUuid, sectors: contractRouteSectors, resolve, reject});
                  } else {
                    reject();
                  }
                })
                : undefined,
              onRowUpdate: state.canEdit && !readOnly
              ?(newData, oldData) =>
                new Promise((resolve, reject) => {
                  const sectorIdx = __.findIndex(contractRouteSectors, (trip) => {
                    const tripData = __.omit(trip, ["tableData"]);
                    const oldRecord = __.omit(oldData, ["tableData"]);
                    return __.isEqual(tripData, oldRecord);
                  });
                  if (sectorIdx >= 0) {
                    contractRouteSectors[sectorIdx] = {...newData};
                    props.onChange({fuelAnnexSectorUuid: selectedRouteUuid, sectors: contractRouteSectors, resolve, reject});
                  } else {
                    reject();
                  }
                })
                : null,
              onRowDelete: state.canEdit && !readOnly
              ?(oldData) =>
                new Promise((resolve, reject) => {
                  const sectorIdx = __.findIndex(contractRouteSectors, (trip) => {
                    const tripData = __.omit(trip, ["tableData"]);
                    const oldRecord = __.omit(oldData, ["tableData"]);
                    return __.isEqual(tripData, oldRecord);
                  });
                  if (sectorIdx >= 0) {
                    contractRouteSectors.splice(sectorIdx, 1);
                    props.onChange({fuelAnnexSectorUuid: selectedRouteUuid, sectors: contractRouteSectors, resolve, reject});
                  } else {
                    reject();
                  }
                })
                :null,
            }}
        />
      </div>
  );
}

export default ContractRouteSectorEditor;


